/**
 * persistence/managers/SessionManager.js
 *
 * CRUD operations for the `sessions` IndexedDB table.
 *
 * A "session" is one analyst's work on one match.  The key is the compound
 * (matchId, analystId) pair — the same pair used for every IndexedDB query.
 *
 * The session record is the primary crash-recovery artifact.  On InputBoard
 * mount, RecoveryManager reads it and compares updatedAt vs lastApiSyncAt
 * to decide whether to offer a local-string restore.
 *
 * All methods are async and safe to call concurrently — Dexie serialises
 * IndexedDB transactions internally.
 */

import { sessionsTable } from "../db/database";
import { SESSION_WRITE_DEBOUNCE_MS } from "../config";
import { countEvents } from "./DiffManager";

// ─── Debounce helper ──────────────────────────────────────────────────────────

/**
 * Module-level debounce map.
 * Key: `${matchId}__${analystId}` → timer handle
 * Prevents flooding IndexedDB with a write on every single keystroke-level Redux action.
 */
const writeTimers = new Map();

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Creates or updates the session record for a (matchId, analystId) pair.
 * The write is debounced by SESSION_WRITE_DEBOUNCE_MS to avoid
 * overwhelming IndexedDB during fast consecutive Redux dispatches.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString  Latest event string from Redux
 */
export function upsertSession({ matchId, analystId, currentString }) {
  const key = `${matchId}__${analystId}`;

  // Cancel the previous pending write for this session
  if (writeTimers.has(key)) {
    clearTimeout(writeTimers.get(key));
  }

  // Schedule the write
  const timer = setTimeout(
    () => _flushSessionWrite({ matchId, analystId, currentString }),
    SESSION_WRITE_DEBOUNCE_MS
  );

  writeTimers.set(key, timer);
}

/**
 * Immediately writes the session record, bypassing the debounce.
 * Called on beforeunload and visibilitychange to ensure the last state
 * is captured before the page teardown.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.currentString
 */
export async function flushSessionWrite({ matchId, analystId, currentString }) {
  const key = `${matchId}__${analystId}`;

  // Cancel any pending debounced write — we're writing immediately
  if (writeTimers.has(key)) {
    clearTimeout(writeTimers.get(key));
    writeTimers.delete(key);
  }

  await _flushSessionWrite({ matchId, analystId, currentString });
}

/**
 * Retrieves the session record for a (matchId, analystId) pair.
 * Returns null if no session has been started for this pair.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<object|null>}
 */
export async function getSession(matchId, analystId) {
  try {
    const record = await sessionsTable()
      .where("[matchId+analystId]")
      .equals([matchId, analystId])
      .first();
    return record || null;
  } catch (error) {
    console.warn("[SessionManager] getSession failed:", error.message);
    return null;
  }
}

/**
 * Updates only the lastApiSyncAt timestamp on the session record.
 * Called by SyncManager after a successful REST API push.
 *
 * @param {string} matchId
 * @param {string} analystId
 */
export async function markApiSynced(matchId, analystId) {
  try {
    const record = await getSession(matchId, analystId);
    if (record) {
      await sessionsTable().update(record.id, {
        lastApiSyncAt: Date.now(),
      });
    }
  } catch (error) {
    console.warn("[SessionManager] markApiSynced failed:", error.message);
  }
}

/**
 * Marks the session as ended.  Called on beforeunload / session cleanup.
 *
 * @param {string} matchId
 * @param {string} analystId
 */
export async function endSession(matchId, analystId) {
  try {
    const record = await getSession(matchId, analystId);
    if (record) {
      await sessionsTable().update(record.id, {
        status: "ended",
        updatedAt: Date.now(),
      });
    }
  } catch (error) {
    console.warn("[SessionManager] endSession failed:", error.message);
  }
}

/**
 * Returns all session records, ordered by most recently updated.
 * Used by the history screen.
 *
 * @returns {Promise<object[]>}
 */
export async function getAllSessions() {
  try {
    return await sessionsTable().orderBy("updatedAt").reverse().toArray();
  } catch (error) {
    console.warn("[SessionManager] getAllSessions failed:", error.message);
    return [];
  }
}

// ─── Private ──────────────────────────────────────────────────────────────────

/**
 * Performs the actual IndexedDB write for upsertSession.
 * Uses Dexie's `put` which is an upsert — creates or replaces.
 * We find the existing record first to preserve its `id` (required for `put`
 * to update rather than insert a duplicate).
 */
async function _flushSessionWrite({ matchId, analystId, currentString }) {
  try {
    const existing = await getSession(matchId, analystId);
    const now = Date.now();
    const eventCount = countEvents(currentString);

    if (existing) {
      // Update in place — preserves id, createdAt, lastApiSyncAt, lastCheckpointAt
      await sessionsTable().update(existing.id, {
        currentString,
        eventCount,
        updatedAt: now,
        status: "active",
      });
    } else {
      // New session record
      await sessionsTable().add({
        matchId,
        analystId,
        currentString,
        eventCount,
        createdAt: now,
        updatedAt: now,
        lastApiSyncAt: null,      // not yet synced to API
        lastCheckpointAt: null,   // not yet checkpointed
        status: "active",
      });
    }
  } catch (error) {
    // Non-fatal — the app works fine without IndexedDB persistence
    console.warn("[SessionManager] _flushSessionWrite failed:", error.message);
  }
}
